import React, {useMemo, useState} from 'react';
import {
	Avatar,
	Chip,
	CircularProgress,
	IconButton,
	InputAdornment,
	List,
	ListItemButton,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import {useTranslation} from "react-i18next";
import TooltipCustom from "@ui/tooltipCustom/TooltipCustom";
import {useAccountStyle} from "@containers/accountStyle/hooks/useAccountStyle";
import {accountInitials} from "@containers/adminAccounts/helpers/initials";
import {
	useConnectToAccountQuery,
	useSwitchableUsersQuery
} from "@containers/adminAccounts/hooks/useAdminAccountsQuery";
import {ISwitchableAccount, ISwitchableUser} from "@containers/adminAccounts/types";
import SwitchConfirmBar from "@containers/adminAccounts/switcher/SwitchConfirmBar";

interface IProps {
	account: ISwitchableAccount,
	onBack: () => void,
}

const matches = (user: ISwitchableUser, query: string) =>
	[user.login, user.name, user.role].some((value) => value?.toLowerCase().includes(query))

/** The second step of the account switcher: whom to be in the chosen account. */
const SwitcherUsers = ({account, onBack}: IProps) => {

	const {t} = useTranslation()
	const {tokens} = useAccountStyle()
	const [query, setQuery] = useState('')
	// The marked user: login empty for the account admin; the switch waits for the confirmation.
	const [chosen, setChosen] = useState<{ login?: string } | null>(null)

	const {data: users, isLoading} = useSwitchableUsersQuery(account.id)
	const {mutate: connect, isPending, variables} = useConnectToAccountQuery()

	const shown = useMemo(() => {
		const needle = query.trim().toLowerCase()
		return (users ?? []).filter((user) => !needle || matches(user, needle))
	}, [users, query])

	const onPick = (login?: string) => {
		if (isPending) return
		setChosen({login})
	}

	const onConfirm = () => chosen && connect({code: account.code, login: chosen.login})

	// Enter marks the first match, a second Enter confirms it.
	const onSearchKeyDown = (e: React.KeyboardEvent) => {
		if (e.key !== 'Enter' || isPending) return
		if (chosen) {
			onConfirm()
			return
		}
		const first = shown.find((user) => user.active)
		first && onPick(first.login)
	}

	const pendingLogin = isPending ? (variables?.login ?? '') : null

	return (
		<>
			<Stack sx={{background: tokens.panelBackground, color: tokens.text, px: 3, pt: 2, pb: 2}} gap={1.5}>
				<Stack direction={'row'} alignItems={'center'} gap={1}>
					<TooltipCustom title={t('adminAccounts.switcherBack')}>
						<IconButton onClick={onBack} disabled={isPending} sx={{color: tokens.text, ml: -1}}>
							<ArrowBackIcon/>
						</IconButton>
					</TooltipCustom>
					<Stack sx={{minWidth: 0}}>
						<Typography sx={{fontSize: 18, fontWeight: 700, lineHeight: 1.3, overflowWrap: 'break-word'}}>
							{t('adminAccounts.switcherUsersTitle', {code: account.code})}
						</Typography>
						{account.description && <Typography noWrap sx={{fontSize: 13, color: tokens.textMuted}}>
							{account.description}
						</Typography>}
					</Stack>
				</Stack>
				<TextField
					autoFocus
					size={'small'}
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onKeyDown={onSearchKeyDown}
					placeholder={t('adminAccounts.switcherUsersSearch')}
					InputProps={{
						startAdornment: <InputAdornment position={'start'}><SearchIcon fontSize={'small'}/></InputAdornment>,
						sx: {bgcolor: 'background.paper', borderRadius: 2},
					}}
				/>
			</Stack>

			<List dense disablePadding sx={{overflowY: 'auto', flex: 1, pb: 0.5}}>
				{!query && <ListItemButton
					selected={!!chosen && !chosen.login}
					disabled={isPending && pendingLogin !== ''}
					onClick={() => onPick()}
					sx={{px: 3, py: 0.75, gap: 1.5}}
				>
					<Avatar variant={'rounded'} sx={{width: 30, height: 30, bgcolor: alpha(tokens.accent, 0.14), color: tokens.accent}}>
						<AdminPanelSettingsIcon fontSize={'small'}/>
					</Avatar>
					<Stack sx={{flex: 1, minWidth: 0}}>
						<Typography sx={{fontSize: 14, fontWeight: 600, lineHeight: 1.35}}>admin</Typography>
						<Typography sx={{fontSize: 12, lineHeight: 1.35}} color={'text.secondary'}>
							{t('adminAccounts.switcherAsAdmin')}
						</Typography>
					</Stack>
					{pendingLogin === '' && <CircularProgress size={16}/>}
				</ListItemButton>}

				{shown.map((user) => (
					<ListItemButton
						key={user.id}
						selected={chosen?.login === user.login}
						disabled={!user.active || (isPending && pendingLogin !== user.login)}
						onClick={() => onPick(user.login)}
						sx={{px: 3, py: 0.5, gap: 1.5}}
					>
						<Avatar variant={'rounded'} sx={{
							width: 30,
							height: 30,
							fontSize: 12,
							fontWeight: 700,
							bgcolor: 'action.selected',
							color: 'text.primary',
						}}>
							{accountInitials(user.name || user.login)}
						</Avatar>
						<Stack sx={{flex: 1, minWidth: 0}}>
							<Typography noWrap sx={{fontSize: 14, fontWeight: 600, lineHeight: 1.35}}>{user.login}</Typography>
							{user.name && <Typography noWrap sx={{fontSize: 12, lineHeight: 1.35}} color={'text.secondary'}>
								{user.name}
							</Typography>}
						</Stack>
						<Stack direction={'row'} gap={0.5} alignItems={'center'} sx={{flexShrink: 0}}>
							{user.role && <Chip size={'small'} variant={'outlined'} label={user.role}/>}
							{!user.active && <Chip size={'small'} label={t('adminSystem.inactive')}/>}
							{pendingLogin === user.login && <CircularProgress size={16}/>}
						</Stack>
					</ListItemButton>
				))}

				{isLoading && <Stack alignItems={'center'} sx={{py: 3}}><CircularProgress size={24}/></Stack>}
				{!isLoading && !shown.length && <Typography sx={{px: 3, py: 2, fontSize: 14}} color={'text.secondary'}>
					{query ? t('adminAccounts.switcherNothing') : t('adminAccounts.switcherNoUsers')}
				</Typography>}
			</List>
			{chosen && <SwitchConfirmBar
				text={chosen.login
					? t('adminAccounts.switcherConfirmUser', {code: account.code, login: chosen.login})
					: t('adminAccounts.switcherConfirm', {code: account.code})}
				pending={isPending}
				onCancel={() => setChosen(null)}
				onConfirm={onConfirm}
			/>}
		</>
	);
};

export default SwitcherUsers;
